import { Seq, Set } from 'immutable';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ViewModelService } from 'src/app/shared/view-model.service';
import {
  GetCardActiveAlertsByCardIdGQL,
  ReceivingFacilityRequestStatus,
  RuleMatchType,
} from 'src/graphql/generated/graphql.generated';
import { NotificationViewModel } from 'src/models/notification-view-model';
import { OperationViewModel } from 'src/models/operation-view-model';
import { RegionViewModel } from 'src/models/region-view-model';
import { RequestViewModel } from 'src/models/request-view-model';
import { TimestampViewModel } from 'src/models/timestamp-view-model';
import { TripLegViewModel } from 'src/models/trip-leg-view-model';
import { TripViewModel } from 'src/models/trip-view-model';
import { UnitViewModel } from 'src/models/unit-view-model';
import { VehicleDispositionViewModel } from 'src/models/vehicle-disposition-view-model';
import { selectViewModel } from 'src/models/view-model-helper';
import {
  selectAcknowledgedActiveAlertsByCardId,
  selectActiveAlertById,
  selectActiveAlertsByCardId,
  selectActiveAlertsByCardIdByMatchType,
  selectOtherActiveAlertsByCardId,
  selectUnacknowledgedActiveAlertsByCardId,
} from 'src/store/entityStores/active-alert-selectors';
import { selectMedicalControlContactLogEntriesByCardId } from 'src/store/entityStores/medical-control-contact-log-entry-slector';
import {
  selectNotificationById,
  selectNotificationsByCardId,
  selectUnsentNotificationsByCardId,
} from 'src/store/entityStores/notification-selectors';
import {
  selectTimestampById,
  selectTimestampsByCardId,
} from 'src/store/entityStores/timestamp-selectors';
import { selectTimestampTypeById } from 'src/store/entityStores/timestamp-type-selectors';
import { ActiveAlertRecord } from 'src/store/generated/active-alert-record.generated';
import { CardRecord, CardViewModelBase } from 'src/store/generated/card-record.generated';
import { selectReceivingFacilityRequestEntitiesByCardId } from 'src/store/generated/receiving-facility-request-record.generated';
import { createObservableLookup, ObservableLookup } from 'src/util/lookup';
import { undefinedOrMap } from 'src/util/rxjs/operators';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { selectTimestampsByTypeAndCardId } from '../store/entityStores/timestamp-selectors';
import { ActiveAlertViewModel } from './active-alert-view-model';
import { CardLinkViewModel } from './card-link-view-model';

export function getNotificationById(
  notificationId: string,
  model: ViewModelService,
  card: CardViewModel | null = null,
) {
  return selectViewModel(
    model.store,
    selectNotificationById(notificationId),
    (notification) => new NotificationViewModel(notification, card, model),
  );
}
export class CardViewModel extends CardViewModelBase {
  public region: RegionViewModel;
  public request$: Observable<RequestViewModel | undefined>;
  public unit$: Observable<UnitViewModel | undefined>;
  public operation$: Observable<OperationViewModel | undefined>;
  public vehicleDisposition$: Observable<VehicleDispositionViewModel | undefined>;
  public timestampIds$: Observable<Seq.Indexed<string>>;
  public notificationIds$: Observable<Seq.Indexed<string>>;
  public unsentNotificationIds$: Observable<Seq.Indexed<string>>;
  public activeAlertIds$: Observable<Seq.Indexed<string>>;
  public unacknowledgedActiveAlertIds$: Observable<Seq.Indexed<string>>;
  public acknowledgedActiveAlertIds$: Observable<Seq.Indexed<string>>;
  public otherActiveAlertIds$: Observable<Seq.Indexed<string>>;
  public medicalControlContactLogEntryIds$: Observable<Seq.Indexed<string>>;
  public receivingFacilityRequestIdsByStatus$: ObservableLookup<
    typeof ReceivingFacilityRequestStatus,
    Set<string>
  >;
  public nonPendingReceivingFacilityRequestIds$: Observable<Seq.Indexed<string>>;
  public cardNumberDisplay$: Observable<string>;
  public cardLinks$: Observable<Seq.Indexed<CardLinkViewModel>>;

  get operationIfExists$(): Observable<OperationViewModel> {
    return this.operation$.pipe(filterNull());
  }
  get tripIfExists$(): Observable<TripViewModel> {
    return this.operationIfExists$.pipe(switchMap((o) => o.tripIfExists$));
  }
  get trip$(): Observable<TripViewModel | undefined> {
    return this.operation$.pipe(
      switchMap((o) => {
        if (o == null) {
          return of(undefined);
        } else {
          return o.trip$;
        }
      }),
    );
  }
  get firstTripLeg$(): Observable<TripLegViewModel | undefined> {
    return this.trip$.pipe(
      switchMap((t) => {
        if (t == null) {
          return of(undefined);
        } else {
          return t.firstTripLeg$;
        }
      }),
    );
  }

  get requestIfExists$() {
    return this.request$.pipe(filterNull());
  }

  get regionId() {
    return this.region.id;
  }

  get nav() {
    return this._model.nav.currentCompany.ops.card(this.cardNumber);
  }

  get linkedCard$(): Observable<CardViewModel | undefined> {
    if (this.linkedCardId == null) {
      return of(undefined);
    }

    return this._model.getCardById(this.linkedCardId);
  }

  // NOTE: Ideally our UI should be referring to Commands.{CommandType}.isAvailable to determine
  // whether to hide or render an action as disabled
  get isReadOnly() {
    return this.isClosed || this._model.opsReadonly.hasReadonlyAccess;
  }

  get isClosedOrCancelled$(): Observable<boolean> {
    if (this.isClosed) {
      return of(true);
    }

    return combineLatest([this.operation$, this.request$]).pipe(
      debounceTime(10),
      map(([oper, req]) => {
        if (oper?.isCancelled === true) {
          return true;
        }

        if (req?.isCancelled === true) {
          return true;
        }

        return false;
      }),
    );
  }

  constructor(card: CardRecord, region: RegionViewModel, private _model: ViewModelService) {
    super(card);
    this.region = region;
    this.unit$ = _model.getUnitForCard(this.cardNumber);
    this.operation$ =
      this.operationId != null ? _model.getOperationById(this.operationId) : of(undefined);
    this.request$ = this.requestId != null ? _model.getRequestById(this.requestId) : of(undefined);
    this.vehicleDisposition$ = this.unit$.pipe(
      undefinedOrMap((u) => {
        if (u.vehicle == null) {
          return undefined;
        }
        return u.vehicle.vehicleDisposition;
      }),
    );
    this.timestampIds$ = _model.store
      .select(selectTimestampsByCardId(card.id))
      .pipe(map((timestamps) => timestamps.map((ts) => ts.id)));

    this.notificationIds$ = _model.store
      .select(selectNotificationsByCardId(card.id))
      .pipe(map((notifications) => notifications.map((n) => n.id)));

    this.unsentNotificationIds$ = _model.store
      .select(selectUnsentNotificationsByCardId(card.id))
      .pipe(map((notifications) => notifications.map((n) => n.id)));

    this.activeAlertIds$ = _model.store
      .select(selectActiveAlertsByCardId(card.id))
      .pipe(map((activeAlerts) => activeAlerts.map((aa) => aa.id)));

    this.unacknowledgedActiveAlertIds$ = _model.store
      .select(selectUnacknowledgedActiveAlertsByCardId(card.id))
      .pipe(map((unacknowledgedActiveAlertIds) => unacknowledgedActiveAlertIds.map((ua) => ua.id)));

    this.acknowledgedActiveAlertIds$ = _model.store
      .select(selectAcknowledgedActiveAlertsByCardId(card.id))
      .pipe(map((acknowledgedActiveAlertIds) => acknowledgedActiveAlertIds.map((aa) => aa.id)));

    this.otherActiveAlertIds$ = _model.store
      .select(selectOtherActiveAlertsByCardId(card.id))
      .pipe(map((otherActiveAlerts) => otherActiveAlerts.map((oa) => oa.id)));

    this.nonPendingReceivingFacilityRequestIds$ = _model.store
      .select(selectReceivingFacilityRequestEntitiesByCardId(this.id))
      .pipe(
        map((requests) =>
          requests
            .filter((r) => r.status !== ReceivingFacilityRequestStatus.Pending)
            .sort((a, b) => {
              return a.dateCreated > b.dateCreated ? -1 : 1;
            })
            .map((rfr) => rfr.id),
        ),
      );

    this.medicalControlContactLogEntryIds$ = _model.store
      .select(selectMedicalControlContactLogEntriesByCardId(card.id))
      .pipe(
        map((medicalControlContactLogEntries) =>
          medicalControlContactLogEntries.map((le) => le.id),
        ),
      );

    this.receivingFacilityRequestIdsByStatus$ = createObservableLookup(
      ReceivingFacilityRequestStatus,
      (status) =>
        this._model.store.select(selectReceivingFacilityRequestEntitiesByCardId(this.id)).pipe(
          distinctUntilChanged(),
          map((rfrs) =>
            rfrs
              .filter((r) => r.status === status)
              .sort((a, b) => {
                return a.dateCreated < b.dateCreated ? -1 : 1;
              })
              .map((c) => c.id)
              .toSet(),
          ),
          distinctUntilChanged(),
        ),
    );

    this.cardNumberDisplay$ = this.operation$.pipe(
      map((o) => this.cardNumber + (o != null && o.isOnStandby ? '*' : '')),
    );

    this.cardLinks$ =
      this.linkedCardId == null
        ? of(Seq.Indexed<CardLinkViewModel>())
        : this._model.getCardLinksByFromCardId(this.linkedCardId);
  }

  public getTimestampById(timestampId: string): Observable<TimestampViewModel | undefined> {
    return this._model.store
      .select(selectTimestampById(timestampId))
      .pipe(
        switchMap((ts) =>
          ts == null
            ? of(undefined as TimestampViewModel | undefined)
            : this._model.store
                .select(selectTimestampTypeById(ts.typeId))
                .pipe(
                  map((tt) =>
                    tt == null ? undefined : new TimestampViewModel(ts, tt, this, this._model),
                  ),
                ),
        ),
      );
  }

  public getTimestampByType(timestampTypeId: string): Observable<TimestampViewModel | undefined> {
    return this._model.store
      .select(selectTimestampTypeById(timestampTypeId))
      .pipe(
        switchMap((tt) =>
          tt == null
            ? of(undefined as TimestampViewModel | undefined)
            : this._model.store
                .select(selectTimestampsByTypeAndCardId(this.id, timestampTypeId))
                .pipe(
                  map((ts) =>
                    ts == null ? undefined : new TimestampViewModel(ts, tt, this, this._model),
                  ),
                ),
        ),
      );
  }

  public getNotificationById(notificationId: string) {
    return getNotificationById(notificationId, this._model, this);
  }

  public getActiveAlertById(activeAlertId: string) {
    return selectViewModel(
      this._model.store,
      selectActiveAlertById(activeAlertId),
      (activeAlert) => new ActiveAlertViewModel(activeAlert, this._model, this),
    );
  }

  public getAllActiveAlerts(ruleMatchType?: RuleMatchType) {
    if (ruleMatchType == null) {
      return of(Seq.Indexed<ActiveAlertRecord>());
    } else {
      if (ruleMatchType === RuleMatchType.Unspecified) {
        return this._model.activeAlerts.getMany({
          cacheSelector: selectActiveAlertsByCardId(this.id),
          getIdsFallback: fetchCardActiveAlertIdsByCardId(
            this.id,
            this._model.activeAlertsByCardId,
          ),
        });
      } else {
        return this._model.activeAlerts.getMany({
          cacheSelector: selectActiveAlertsByCardIdByMatchType(this.id, ruleMatchType),
          getIdsFallback: fetchCardActiveAlertIdsByCardId(
            this.id,
            this._model.activeAlertsByCardId,
            ruleMatchType,
          ),
        });
      }
    }
  }
}

function fetchCardActiveAlertIdsByCardId(
  cardId: string,
  activeAlertsByCardId: GetCardActiveAlertsByCardIdGQL,
  ruleMatchType?: RuleMatchType,
) {
  return activeAlertsByCardId.fetch({ cardId }).pipe(
    map((res) => res.data.cards.byId),
    map((card) => (card == null ? [] : card.activeAlerts)),
    map((activeAlerts) =>
      activeAlerts
        .filter((aa) => ruleMatchType == null || aa.matchType === ruleMatchType)
        .map((aa) => aa.id),
    ),
  );
}
